import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { PortfolioConfig } from '../types/config';
import { extractCategories } from '../utils/configLoader';
import D3CloudComponent from './D3CloudComponent';

interface SkillsProps {
  config: PortfolioConfig;
}


// Star Rating Component
const StarRating: React.FC<{ rating: number }> = ({ rating }) => {
  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-4 h-4 ${
            star <= rating 
              ? 'text-primary-500 fill-current' 
              : 'text-gray-300 dark:text-gray-600'
          }`}
          viewBox="0 0 20 20"
        >
          <defs>
            <linearGradient id={`starGradient-${star}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#a855f7" />
            </linearGradient>
          </defs>
          <path 
            d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" 
            fill={star <= rating ? `url(#starGradient-${star})` : undefined}
          />
        </svg>
      ))}
    </div>
  );
};

// Skills Accordion Component
interface SkillsAccordionProps {
  category: string;
  skills: Array<{ name: string; category: string; rating: number }>;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}

const SkillsAccordion: React.FC<SkillsAccordionProps> = ({ category, skills, index, isOpen, onToggle }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="bg-white/60 dark:bg-dark-800/60 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 dark:border-dark-700/20 overflow-hidden"
    >
      {/* Accordion Header */}
      <motion.button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/20 dark:hover:bg-dark-700/20 transition-colors duration-200"
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <div className="flex items-center space-x-3 min-w-0">
          <h3 className="text-lg sm:text-xl font-bold text-dark-800 dark:text-white truncate">
            {category}
          </h3>
          <span className="flex-shrink-0 text-xs sm:text-sm text-dark-500 dark:text-dark-400 bg-dark-100 dark:bg-dark-700 px-2 py-1 rounded-full whitespace-nowrap">
            {skills.length} skills
          </span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="flex-shrink-0 ml-2 text-dark-600 dark:text-dark-300"
        >
          <ChevronDown className="w-5 h-5" />
        </motion.div>
      </motion.button>

      {/* Accordion Content */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-4 space-y-3">
              {skills.map((skill, skillIndex) => (
                <motion.div
                  key={skill.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: skillIndex * 0.05 }}
                  className="flex items-center justify-between py-2 border-b border-white/20 dark:border-dark-700/20 last:border-b-0"
                >
                  <span className="text-dark-600 dark:text-dark-300 font-medium">
                    {skill.name}
                  </span>
                  <StarRating rating={skill.rating} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const Skills: React.FC<SkillsProps> = ({ config }) => {
  // Collect all unique technologies from projects and about skills
  const allTechnologies = Array.from(
    new Set([
      ...config.about.skills.map(skill => skill.name),
      ...config.projects.flatMap(project => project.technologies)
    ])
  );

  // Extract unique categories from skills data
  const categories = extractCategories(config.about.skills);

  // Create different size categories for technologies based on rating
  const getTechSize = (tech: string, index: number) => {
    // Find the skill in the config to get its rating
    const skill = config.about.skills.find(s => s.name === tech);
    
    if (skill?.rating === 5) return 'text-4xl md:text-5xl';
    if (skill?.rating === 4) return 'text-2xl md:text-3xl';
    return 'text-lg md:text-xl';
  };

  // State to track which accordion is open
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  const handleAccordionToggle = (category: string) => {
    setOpenAccordion(openAccordion === category ? null : category);
  };

  return (
    <section id="skills" className="section-padding from-slate-50 via-blue-50 to-indigo-50 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 dark:opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(99, 102, 241, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.2) 0%, transparent 50%)`,
          backgroundSize: '400px 400px, 300px 300px, 500px 500px',
          backgroundPosition: '0 0, 200px 200px, 100px 100px'
        }}></div>
      </div>

      {/* Floating geometric shapes */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-br from-primary-400/20 to-purple-400/20 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute top-40 right-20 w-16 h-16 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-xl animate-pulse" style={{animationDelay: '1s'}}></div>
      <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-pulse" style={{animationDelay: '2s'}}></div>

      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Skills & <span className="gradient-text">Technologies</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-dark-600 dark:text-dark-300 max-w-2xl mx-auto">
            Interactive exploration of my technical expertise - hover, drag, and discover the technologies I work with
          </p>
        </motion.div>

        {/* Bento Grid Layout */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="grid lg:grid-cols-10 gap-8 lg:gap-12"
        >
          {/* Left Side - Tag Cloud (70%) */}
          <div className="lg:col-span-7 bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-3xl p-6 lg:p-8 shadow-2xl border border-white/20 dark:border-dark-700/20">
            <D3CloudComponent 
              technologies={allTechnologies} 
              getTechSize={getTechSize}
              config={config}
            />
          </div>

          {/* Right Side - Skills Accordions (30%) */}
          <div className="lg:col-span-3 space-y-4">
            {categories.map((category, index) => {
              const categorySkills = config.about.skills.filter(skill => skill.category === category);
              if (categorySkills.length === 0) return null;
              
              return (
                <SkillsAccordion 
                  key={category} 
                  category={category} 
                  skills={categorySkills}
                  index={index}
                  isOpen={openAccordion === category}
                  onToggle={() => handleAccordionToggle(category)}
                />
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills; 